import { asCategoryId, asItemId, asManagedProfileId, asNoteId, asTemplateId, asTodoId, asTripId, asUserId, between, compareByOrder, createSubject, newId, ok, err, notFound, conflict, permissionDenied, } from "@pack-it-up/shared";
import { emptyCompletion, isItemComplete, participantsOf } from "@pack-it-up/core";
// ---------------------------------------------------------------------------
// Keyed subjects: one BehaviorSubject per observe key, created lazily.
// ---------------------------------------------------------------------------
class Keyed {
    subs = new Map();
    observe(key, compute) {
        let s = this.subs.get(key);
        if (!s) {
            s = createSubject(compute());
            this.subs.set(key, s);
        }
        return { subscribe: (next) => s.subscribe(next) };
    }
    // Re-emit recomputed values for the given keys (no-op for unsubscribed keys).
    emit(key, value) {
        this.subs.get(key)?.next(value);
    }
}
const pairKey = (a, b) => [a, b].sort().join("_");
// Doc 14 §5 — apply a refresh patch to a row: `null` clears, an absent key is left
// alone. The Firestore applier does the same through `patchField`/`deleteField`.
const applyRowPatch = (row, patch) => {
    const next = { ...row };
    for (const [k, v] of Object.entries(patch)) {
        if (v === null)
            delete next[k];
        else
            next[k] = v;
    }
    return next;
};
export const makeInMemoryRepos = () => {
    // ---- backing tables ----
    const users = new Map();
    const lookupByEmail = new Map();
    const requests = new Map();
    const friendships = new Map();
    const friendCopies = new Map();
    const managed = new Map();
    const trips = new Map();
    const notes = new Map();
    const categories = new Map();
    const items = new Map();
    const todos = new Map();
    // item/todo id -> the id of the trip/template that OWNS it (i.e. its category's
    // `parentId`), NOT the category. Items/todos are observed per owning parent, so
    // this is the routing key for re-emitting the right `observeItems`/`observeTodos`
    // stream when a row changes.
    const itemParent = new Map();
    const todoParent = new Map();
    const templates = new Map();
    const pendingInvites = new Map();
    const inviteLinks = new Map();
    // ---- observers ----
    const currentUser = createSubject(null);
    const friendsObs = new Keyed();
    const incomingObs = new Keyed();
    const outgoingObs = new Keyed();
    const managedObs = new Keyed();
    const myProfileObs = new Keyed();
    const mySettingsObs = new Keyed();
    const tripObs = new Keyed();
    const myTripsObs = new Keyed(); // key: `${userId}:${archived}`
    const notesObs = new Keyed();
    const categoriesObs = new Keyed();
    const itemsObs = new Keyed();
    const todosObs = new Keyed();
    const myTemplatesObs = new Keyed();
    const pendingInvitesObs = new Keyed(); // key: resource ref
    const inviteLinksObs = new Keyed();
    const connectivityObs = createSubject(true);
    // ---- test seams ----
    let latency = 0;
    const failures = new Map();
    const schedule = globalThis.setTimeout;
    const guard = async (method) => {
        if (latency > 0)
            await new Promise((r) => schedule(() => r(), latency));
        const f = failures.get(method);
        if (f) {
            failures.delete(method);
            return f;
        }
        return null;
    };
    let actingUserId = null;
    const me = () => {
        if (actingUserId == null)
            throw new Error("no current user — signIn/signUp first");
        return actingUserId;
    };
    // ---- recompute helpers ----
    const friendsOf = (u) => [...(friendCopies.get(u)?.values() ?? [])];
    const incomingOf = (u) => [...requests.values()].filter((r) => r.toUserId === u && r.status === "pending");
    const outgoingOf = (u) => [...requests.values()].filter((r) => r.fromUserId === u && r.status === "pending");
    const managedOf = (u) => [...managed.values()].filter((p) => p.guardianUserIds.includes(u));
    const refKey = (r) => `${r.kind}:${r.id}`;
    const tripSummary = (t, role) => {
        const its = [...items.values()].filter((i) => itemParent.get(i.id) === t.id);
        const tds = [...todos.values()].filter((td) => todoParent.get(td.id) === t.id);
        const parts = participantsOf(t);
        const donePacking = its.filter((i) => isItemComplete(i, parts)).length;
        const doneTodos = tds.filter((td) => isItemComplete(td, parts)).length;
        return {
            id: t.id,
            name: t.name,
            ...(t.startDate !== undefined ? { startDate: t.startDate } : {}),
            ...(t.endDate !== undefined ? { endDate: t.endDate } : {}),
            ...(t.color !== undefined ? { color: t.color } : {}),
            role,
            archived: t.archived,
            itemCount: its.length,
            todoCount: tds.length,
            packingProgress: its.length ? donePacking / its.length : 0,
            todoProgress: tds.length ? doneTodos / tds.length : 0,
            ...(t.location !== undefined ? { location: t.location } : {}),
            people: Object.values(t.members).map((mem) => ({ displayName: mem.profile.displayName })),
        };
    };
    const roleOf = (t, u) => {
        const m = t.members[u];
        return m && m.kind === "user" ? m.role : null;
    };
    const myTrips = (u, archived) => [...trips.values()]
        .filter((t) => roleOf(t, u) != null && t.archived === archived)
        .map((t) => tripSummary(t, roleOf(t, u)));
    const emitMyTrips = (u) => {
        myTripsObs.emit(`${u}:false`, myTrips(u, false));
        myTripsObs.emit(`${u}:true`, myTrips(u, true));
    };
    // Re-emit my-trips for every member of a trip (counts/progress changed).
    const emitTripMembers = (t) => {
        for (const id of Object.keys(t.members)) {
            const m = t.members[id];
            if (m && m.kind === "user")
                emitMyTrips(id);
        }
    };
    // Emit immutable copies — like a real backend, observers must never receive a
    // reference the store later sees mutated in place.
    const clone = (v) => structuredClone(v);
    const childrenCats = (parentId) => [...categories.values()]
        .filter((c) => c.parentId === parentId)
        .sort(compareByOrder)
        .map(clone);
    const itemsOfTrip = (tripId) => [...items.values()].filter((i) => itemParent.get(i.id) === tripId).map(clone);
    const todosOfTrip = (tripId) => [...todos.values()].filter((td) => todoParent.get(td.id) === tripId).map(clone);
    const notesOfList = (listId) => [...notes.values()].filter((n) => n.listId === listId).map(clone);
    const myTemplates = (u, archived) => [...templates.values()]
        .filter((t) => t.members[u]?.role != null && t.archived === archived)
        .map((t) => ({
        id: t.id,
        name: t.name,
        ...(t.color !== undefined ? { color: t.color } : {}),
        ...(t.emoji !== undefined ? { emoji: t.emoji } : {}),
        role: t.members[u].role,
        archived: t.archived,
        participants: clone(t.participants),
    }));
    const pendingFor = (r, inviter) => [...pendingInvites.values()].filter((p) => refKey(p.resource) === refKey(r) && p.invitedBy === inviter && p.status === "pending");
    const linksFor = (r) => [...inviteLinks.values()].filter((l) => refKey(l.resource) === refKey(r) && !l.revoked);
    const toAuthUser = (rec) => ({
        id: rec.profile.userId,
        email: rec.profile.email,
        displayName: rec.profile.displayName,
        ...(rec.profile.photoUrl !== undefined ? { photoUrl: rec.profile.photoUrl } : {}),
        isAnonymous: rec.anonymous === true,
        providers: [...rec.providers],
        emailVerified: rec.emailVerified,
    });
    // Mirrors real Firebase: a password sign-up is unverified until the emailed link is
    // clicked; a federated provider (Google/Apple) vouches for the address, so it's
    // verified immediately.
    const createUser = (email, password, displayName, providers = ["password"]) => {
        const id = asUserId(newId());
        users.set(id, {
            profile: { userId: id, displayName, email },
            password,
            providers,
            timezone: "UTC",
            reminderSendHour: 7,
            defaultStagingOptIn: false,
            itemDisplay: "condensed",
            emailVerified: !providers.includes("password"),
        });
        lookupByEmail.set(email.toLowerCase(), id);
        friendCopies.set(id, new Map());
        return id;
    };
    // Firebase-style provider ids ("google.com", "apple.com", "password", or the
    // custom id of a generic OIDC provider).
    const providerIdOf = (p) => {
        switch (p.kind) {
            case "password":
                return "password";
            case "google":
                return "google.com";
            case "apple":
                return "apple.com";
            case "oidc":
                return p.providerId;
        }
    };
    // What the fake "popup" returns per provider id — seeded by tests.
    const providerIdentities = new Map();
    // =========================================================================
    // Repository implementations
    // =========================================================================
    const auth = {
        observeCurrentUser: () => ({ subscribe: (next) => currentUser.subscribe(next) }),
        async signUp(email, password, displayName) {
            const f = await guard("signUp");
            if (f)
                return err("Network");
            if (lookupByEmail.has(email.toLowerCase()))
                return err("EmailInUse");
            const id = createUser(email, password, displayName);
            actingUserId = id;
            const u = toAuthUser(users.get(id));
            currentUser.next(u);
            return ok(u);
        },
        async signIn(email, password) {
            const f = await guard("signIn");
            if (f)
                return err("Network");
            const id = lookupByEmail.get(email.toLowerCase());
            if (id == null)
                return err("UserNotFound");
            const rec = users.get(id);
            if (!rec.providers.includes("password"))
                return err("NoPasswordCredential");
            if (rec.password !== password)
                return err("WrongPassword");
            actingUserId = id;
            const u = toAuthUser(rec);
            currentUser.next(u);
            return ok(u);
        },
        async signInAnonymously() {
            const f = await guard("signInAnonymously");
            if (f)
                return err("Network");
            // An anonymous record: empty email, no lookupByEmail entry (nothing to sign
            // back into), flagged so joinViaLink's non-anonymous check rejects it.
            const id = asUserId(newId());
            users.set(id, {
                profile: { userId: id, displayName: "Guest", email: "" },
                password: "",
                providers: [],
                timezone: "UTC",
                reminderSendHour: 7,
                defaultStagingOptIn: false,
                itemDisplay: "condensed",
                anonymous: true,
                emailVerified: false,
            });
            friendCopies.set(id, new Map());
            actingUserId = id;
            const u = toAuthUser(users.get(id));
            currentUser.next(u);
            return ok(u);
        },
        // T31 — auth-profile-only rename (no users/{uid} doc side-effects), so an anonymous
        // guest's typed name outlives the one trip it joined.
        async setAuthDisplayName(displayName) {
            const rec = users.get(me());
            if (!rec)
                return err("RequiresRecentLogin");
            rec.profile = { ...rec.profile, displayName };
            const u = toAuthUser(rec);
            currentUser.next(u);
            return ok(u);
        },
        async signOut() {
            actingUserId = null;
            currentUser.next(null);
        },
        async changeEmail(newEmail, currentPassword) {
            const rec = users.get(me());
            if (!rec || rec.password !== currentPassword)
                return err("WrongPassword");
            lookupByEmail.delete(rec.profile.email.toLowerCase());
            rec.profile = { ...rec.profile, email: newEmail };
            lookupByEmail.set(newEmail.toLowerCase(), rec.profile.userId);
            currentUser.next(toAuthUser(rec));
            return ok(undefined);
        },
        async changePassword(currentPassword, newPassword) {
            const rec = users.get(me());
            if (!rec)
                return err("UserNotFound");
            if (!rec.providers.includes("password"))
                return err("NoPasswordCredential");
            if (rec.password !== currentPassword)
                return err("WrongPassword");
            rec.password = newPassword;
            return ok(undefined);
        },
        async setPassword(newPassword) {
            const f = await guard("setPassword");
            if (f)
                return err("Network");
            const rec = users.get(me());
            if (!rec)
                return err("UserNotFound");
            // Mirrors Firebase: linking a password credential onto an account that
            // already has one fails (auth/provider-already-linked, unmapped).
            if (rec.providers.includes("password"))
                return err("Network");
            rec.password = newPassword;
            rec.providers.push("password");
            const u = toAuthUser(rec);
            currentUser.next(u);
            return ok(u);
        },
        async sendPasswordReset(email) {
            const id = lookupByEmail.get(email.toLowerCase());
            if (id == null)
                return err("UserNotFound");
            if (!users.get(id).providers.includes("password"))
                return err("NoPasswordCredential");
            return ok(undefined);
        },
        async sendEmailVerification() {
            const rec = users.get(me());
            if (!rec)
                return err("UserNotFound");
            return ok(undefined);
        },
        async refreshCurrentUser() {
            const rec = users.get(me());
            if (!rec)
                return err("UserNotFound");
            return ok(toAuthUser(rec));
        },
        // --- Federated/OIDC flows, against the seeded provider-identity seam. ---
        async signInWithProvider(provider) {
            const f = await guard("signInWithProvider");
            if (f)
                return err("Network");
            const pid = providerIdOf(provider);
            const identity = providerIdentities.get(pid);
            // No identity registered = the user closed the popup without picking one.
            if (!identity)
                return err("PopupClosed");
            const existingId = lookupByEmail.get(identity.email.toLowerCase());
            let rec;
            if (existingId != null) {
                rec = users.get(existingId);
                // Same email, but this provider isn't linked to the account (Doc 04 §1).
                if (!rec.providers.includes(pid))
                    return err("AccountExistsWithDifferentCredential");
            }
            else {
                // First use: sign-up path — a fresh account backed only by this provider.
                const id = createUser(identity.email, "", identity.displayName, [pid]);
                rec = users.get(id);
            }
            actingUserId = rec.profile.userId;
            const u = toAuthUser(rec);
            currentUser.next(u);
            return ok(u);
        },
        async linkProvider(provider) {
            const f = await guard("linkProvider");
            if (f)
                return err("Network");
            const rec = users.get(me());
            if (!rec)
                return err("UserNotFound");
            const pid = providerIdOf(provider);
            const identity = providerIdentities.get(pid);
            if (!identity)
                return err("PopupClosed");
            // The picked identity already backs a *different* account.
            const owner = lookupByEmail.get(identity.email.toLowerCase());
            if (owner != null && owner !== rec.profile.userId)
                return err("AccountExistsWithDifferentCredential");
            if (!rec.providers.includes(pid))
                rec.providers.push(pid);
            const u = toAuthUser(rec);
            currentUser.next(u);
            return ok(u);
        },
        async unlinkProvider(providerId) {
            const f = await guard("unlinkProvider");
            if (f)
                return err("Network");
            const rec = users.get(me());
            if (!rec)
                return err("UserNotFound");
            if (rec.providers.includes(providerId) && rec.providers.length === 1)
                return err("LastSignInMethod");
            rec.providers = rec.providers.filter((p) => p !== providerId);
            if (providerId === "password")
                rec.password = "";
            const u = toAuthUser(rec);
            currentUser.next(u);
            return ok(u);
        },
    };
    // Owner profile/settings, backed by the UserRecord table.
    const profileOf = (u) => {
        const rec = users.get(u);
        return rec ? clone(rec.profile) : null;
    };
    const settingsOf = (u) => {
        const rec = users.get(u);
        return rec
            ? {
                timezone: rec.timezone,
                reminderSendHour: rec.reminderSendHour,
                defaultStagingOptIn: rec.defaultStagingOptIn,
                itemDisplay: rec.itemDisplay,
            }
            : null;
    };
    const profiles = {
        observeMyProfile: (userId) => myProfileObs.observe(userId, () => profileOf(userId)),
        observeMySettings: (userId) => mySettingsObs.observe(userId, () => settingsOf(userId)),
        async writeProfile(patch) {
            const f = await guard("writeProfile");
            if (f)
                return err(f);
            const id = me();
            const rec = users.get(id);
            if (!rec)
                return err(notFound("user", id));
            if (patch.displayName !== undefined)
                rec.profile.displayName = patch.displayName;
            if (patch.photoUrl !== undefined) {
                if (patch.photoUrl === null)
                    delete rec.profile.photoUrl;
                else
                    rec.profile.photoUrl = patch.photoUrl;
            }
            if (patch.timezone !== undefined)
                rec.timezone = patch.timezone;
            if (patch.reminderSendHour !== undefined)
                rec.reminderSendHour = patch.reminderSendHour;
            if (patch.defaultStagingOptIn !== undefined)
                rec.defaultStagingOptIn = patch.defaultStagingOptIn;
            if (patch.itemDisplay !== undefined)
                rec.itemDisplay = patch.itemDisplay;
            myProfileObs.emit(id, profileOf(id));
            mySettingsObs.emit(id, settingsOf(id));
            currentUser.next(toAuthUser(rec));
            // Fan the new public fields out to the friends who hold a denormalised copy —
            // the fake's stand-in for the `onUserProfileWrite` trigger (functions/src/profile.ts).
            for (const [friendUserId, copies] of friendCopies) {
                if (!copies.has(id))
                    continue;
                copies.set(id, clone(rec.profile));
                friendsObs.emit(friendUserId, friendsOf(friendUserId));
            }
            return ok(undefined);
        },
    };
    const friends = {
        observeFriends: (userId) => friendsObs.observe(userId, () => friendsOf(userId)),
        observeIncomingRequests: (userId) => incomingObs.observe(userId, () => incomingOf(userId)),
        observeOutgoingRequests: (userId) => outgoingObs.observe(userId, () => outgoingOf(userId)),
        async lookupByExactEmail(email) {
            const f = await guard("lookupByExactEmail");
            if (f)
                return err(f);
            const id = lookupByEmail.get(email.toLowerCase());
            if (id == null)
                return ok(null);
            const p = users.get(id).profile;
            return ok({ userId: id, displayName: p.displayName, ...(p.photoUrl !== undefined ? { photoUrl: p.photoUrl } : {}) });
        },
        async sendRequest(toUserId) {
            const f = await guard("sendRequest");
            if (f)
                return err(f);
            const from = me();
            if (from === toUserId)
                return err(conflict("cannot friend yourself"));
            if (friendships.has(pairKey(from, toUserId)))
                return err(conflict("already friends"));
            if (outgoingOf(from).some((r) => r.toUserId === toUserId))
                return err(conflict("request already pending"));
            // reopen a previously ignored request, else create
            const existing = [...requests.values()].find((r) => r.fromUserId === from && r.toUserId === toUserId);
            const fromP = users.get(from).profile;
            const toP = users.get(toUserId)?.profile;
            if (existing)
                existing.status = "pending";
            else {
                const id = newId();
                requests.set(id, {
                    id,
                    fromUserId: from,
                    toUserId,
                    status: "pending",
                    createdAt: new Date(),
                    fromProfile: { userId: from, displayName: fromP.displayName },
                    ...(toP ? { toProfile: { userId: toUserId, displayName: toP.displayName } } : {}),
                });
            }
            incomingObs.emit(toUserId, incomingOf(toUserId));
            outgoingObs.emit(from, outgoingOf(from));
            return ok(undefined);
        },
        async acceptRequest(requestId) {
            const f = await guard("acceptRequest");
            if (f)
                return err(f);
            const req = requests.get(requestId);
            if (!req)
                return err(notFound("friendRequest", requestId));
            req.status = "accepted";
            const { fromUserId: a, toUserId: b } = req;
            friendships.set(pairKey(a, b), { userIds: [a, b], createdAt: new Date() });
            friendCopies.get(a).set(b, users.get(b).profile);
            friendCopies.get(b).set(a, users.get(a).profile);
            friendsObs.emit(a, friendsOf(a));
            friendsObs.emit(b, friendsOf(b));
            incomingObs.emit(b, incomingOf(b));
            outgoingObs.emit(a, outgoingOf(a));
            return ok(undefined);
        },
        async ignoreRequest(requestId) {
            const f = await guard("ignoreRequest");
            if (f)
                return err(f);
            const req = requests.get(requestId);
            if (!req)
                return err(notFound("friendRequest", requestId));
            req.status = "ignored";
            incomingObs.emit(req.toUserId, incomingOf(req.toUserId));
            outgoingObs.emit(req.fromUserId, outgoingOf(req.fromUserId));
            return ok(undefined);
        },
        async removeFriend(friendUserId) {
            const f = await guard("removeFriend");
            if (f)
                return err(f);
            const m = me();
            friendships.delete(pairKey(m, friendUserId));
            friendCopies.get(m)?.delete(friendUserId);
            friendCopies.get(friendUserId)?.delete(m);
            friendsObs.emit(m, friendsOf(m));
            friendsObs.emit(friendUserId, friendsOf(friendUserId));
            return ok(undefined);
        },
    };
    const managedRepo = {
        observeMyManagedProfiles: (userId) => managedObs.observe(userId, () => managedOf(userId)),
        async createManagedProfile(input) {
            const f = await guard("createManagedProfile");
            if (f)
                return err(f);
            const id = asManagedProfileId(newId());
            managed.set(id, { id, displayName: input.displayName, guardianUserIds: [me()], createdAt: new Date() });
            managedObs.emit(me(), managedOf(me()));
            return ok(id);
        },
        async updateManagedProfile(patch) {
            const f = await guard("updateManagedProfile");
            if (f)
                return err(f);
            const p = managed.get(patch.id);
            if (!p)
                return err(notFound("managedProfile", patch.id));
            if (patch.displayName !== undefined)
                p.displayName = patch.displayName;
            if (patch.photoUrl !== undefined)
                p.photoUrl = patch.photoUrl;
            for (const g of p.guardianUserIds)
                managedObs.emit(g, managedOf(g));
            return ok(undefined);
        },
        async addGuardian(profileId, friendUserId) {
            const f = await guard("addGuardian");
            if (f)
                return err(f);
            const p = managed.get(profileId);
            if (!p)
                return err(notFound("managedProfile", profileId));
            if (!p.guardianUserIds.includes(friendUserId))
                p.guardianUserIds.push(friendUserId);
            for (const g of p.guardianUserIds)
                managedObs.emit(g, managedOf(g));
            return ok(undefined);
        },
        async removeGuardian(profileId, userId) {
            const f = await guard("removeGuardian");
            if (f)
                return err(f);
            const p = managed.get(profileId);
            if (!p)
                return err(notFound("managedProfile", profileId));
            if (p.guardianUserIds.length <= 1)
                return err(conflict("a managed profile needs at least one guardian"));
            p.guardianUserIds = p.guardianUserIds.filter((g) => g !== userId);
            managedObs.emit(userId, managedOf(userId));
            for (const g of p.guardianUserIds)
                managedObs.emit(g, managedOf(g));
            return ok(undefined);
        },
        async upgradeToAccount(profileId, email) {
            const f = await guard("upgradeToAccount");
            if (f)
                return err(f);
            const p = managed.get(profileId);
            if (!p)
                return err(notFound("managedProfile", profileId));
            // Mint the new principal and fan the id swap across trips + completions.
            const newUserId = createUser(email, newId(), p.displayName);
            for (const t of trips.values()) {
                const m = t.members[profileId];
                if (m && m.kind === "managed") {
                    delete t.members[profileId];
                    t.members[newUserId] = {
                        kind: "user",
                        role: "editor",
                        profile: { displayName: p.displayName, email },
                        settings: { stagingOptIn: false },
                    };
                    tripObs.emit(t.id, t);
                    emitMyTrips(newUserId);
                }
            }
            for (const it of items.values()) {
                if (it.completions.byParticipant[profileId]) {
                    it.completions.byParticipant[newUserId] = it.completions.byParticipant[profileId];
                    delete it.completions.byParticipant[profileId];
                    itemsObs.emit(itemParent.get(it.id), itemsOfTrip(itemParent.get(it.id)));
                }
            }
            p.upgradedToUserId = newUserId;
            // The profile now carries `upgradedToUserId` (tombstoned). Re-emit each
            // guardian's managed-profile list so observers see the upgraded state.
            for (const g of p.guardianUserIds)
                managedObs.emit(g, managedOf(g));
            return ok(undefined);
        },
    };
    // remaining repos in part 2 (composed below)
    const tripRepo = makeTripRepo();
    const noteRepo = makeNoteRepo();
    const categoryRepo = makeCategoryRepo();
    const itemRepo = makeItemRepo();
    const todoRepo = makeTodoRepo();
    const templateRepo = makeTemplateRepo();
    const sharingRepo = makeSharingRepo();
    const connectivity = {
        observeConnectivity: () => ({ subscribe: (next) => connectivityObs.subscribe(next) }),
    };
    function makeTripRepo() {
        return {
            observeTrip: (tripId) => tripObs.observe(tripId, () => trips.get(tripId) ?? null),
            observeMyTrips: (userId, opts) => myTripsObs.observe(`${userId}:${opts.archived}`, () => myTrips(userId, opts.archived)),
            async createTrip(input) {
                const fa = await guard("createTrip");
                if (fa)
                    return err(fa);
                const id = asTripId(newId());
                const owner = me();
                const ownerRecord = users.get(owner);
                const ownerProfile = ownerRecord.profile;
                const trip = {
                    id,
                    name: input.name,
                    ...(input.startDate ? { startDate: input.startDate } : {}),
                    ...(input.endDate ? { endDate: input.endDate } : {}),
                    ...(input.location ? { location: input.location } : {}),
                    ...(input.description ? { description: input.description } : {}),
                    members: {
                        // Issue #8 — a copied trip brings the source's members over; the creator's
                        // own entry is written last and always wins as owner.
                        ...(input.members ?? {}),
                        [owner]: {
                            kind: "user",
                            role: "owner",
                            profile: { displayName: ownerProfile.displayName, email: ownerProfile.email },
                            settings: { stagingOptIn: ownerRecord.defaultStagingOptIn },
                        },
                    },
                    visibility: "private",
                    settings: { allowProxyCompletion: true },
                    importedTemplateIds: [],
                    archived: false,
                    createdAt: new Date(),
                };
                trips.set(id, trip);
                tripObs.emit(id, trip);
                emitMyTrips(owner);
                return ok(id);
            },
            // Same tables as `fetchTemplateRows`, keyed by the trip as parent (Doc 07 §2).
            async fetchTripContents(tripId) {
                const fa = await guard("fetchTripContents");
                if (fa)
                    return err(fa);
                if (!trips.has(tripId))
                    return err(notFound("trip", tripId));
                return ok({ categories: childrenCats(tripId), items: itemsOfTrip(tripId), todos: todosOfTrip(tripId) });
            },
            async fetchTrip(tripId) {
                const fa = await guard("fetchTrip");
                if (fa)
                    return err(fa);
                return ok(trips.get(tripId) ?? null);
            },
            async updateTrip(patch) {
                const fa = await guard("updateTrip");
                if (fa)
                    return err(fa);
                const t = trips.get(patch.tripId);
                if (!t)
                    return err(notFound("trip", patch.tripId));
                if (patch.name !== undefined)
                    t.name = patch.name;
                applyNullable(t, "startDate", patch.startDate);
                applyNullable(t, "endDate", patch.endDate);
                applyNullable(t, "location", patch.location);
                applyNullable(t, "description", patch.description);
                applyNullable(t, "color", patch.color);
                if (patch.visibility !== undefined)
                    t.visibility = patch.visibility;
                if (patch.settings?.allowProxyCompletion !== undefined)
                    t.settings.allowProxyCompletion = patch.settings.allowProxyCompletion;
                tripObs.emit(t.id, t);
                emitTripMembers(t);
                return ok(undefined);
            },
            async setMemberSettings(tripId, userId, settings) {
                const fa = await guard("setMemberSettings");
                if (fa)
                    return err(fa);
                const t = trips.get(tripId);
                if (!t)
                    return err(notFound("trip", tripId));
                const member = t.members[userId];
                if (!member)
                    return err(notFound("member", userId));
                member.settings = settings;
                tripObs.emit(t.id, t);
                emitTripMembers(t);
                return ok(undefined);
            },
            async archiveTrip(tripId, archived) {
                const fa = await guard("archiveTrip");
                if (fa)
                    return err(fa);
                const t = trips.get(tripId);
                if (!t)
                    return err(notFound("trip", tripId));
                t.archived = archived;
                tripObs.emit(t.id, t);
                emitTripMembers(t);
                return ok(undefined);
            },
            async deleteTrip(tripId) {
                const fa = await guard("deleteTrip");
                if (fa)
                    return err(fa);
                const t = trips.get(tripId);
                trips.delete(tripId);
                tripObs.emit(tripId, null);
                if (t)
                    emitTripMembers(t);
                return ok(undefined);
            },
        };
    }
    function makeNoteRepo() {
        return {
            observeNotes: (listId) => notesObs.observe(listId, () => notesOfList(listId)),
            async upsertNote(note) {
                const fa = await guard("upsertNote");
                if (fa)
                    return err(fa);
                const id = (note.id ?? asNoteId(newId()));
                const existing = notes.get(id);
                notes.set(id, {
                    id,
                    listId: note.listId,
                    body: note.body,
                    links: note.links,
                    ...(note.tags !== undefined && note.tags.length > 0 ? { tags: note.tags } : {}),
                    authorId: note.authorId,
                    // Preserve createdAt on update; set to now on create.
                    createdAt: existing?.createdAt ?? Date.now(),
                    // Preserve order on update; prepend on create (new notes land at the top,
                    // same spot the old newest-first sort always put them).
                    order: existing?.order ?? between(null, firstNoteOrder(note.listId)),
                });
                notesObs.emit(note.listId, notesOfList(note.listId));
                return ok(id);
            },
            async deleteNote(noteId) {
                const fa = await guard("deleteNote");
                if (fa)
                    return err(fa);
                const n = notes.get(noteId);
                notes.delete(noteId);
                if (n)
                    notesObs.emit(n.listId, notesOfList(n.listId));
                return ok(undefined);
            },
            async moveNote(noteId, order) {
                const fa = await guard("moveNote");
                if (fa)
                    return err(fa);
                const n = notes.get(noteId);
                if (!n)
                    return err(notFound("note", noteId));
                n.order = order;
                notesObs.emit(n.listId, notesOfList(n.listId));
                return ok(undefined);
            },
        };
    }
    function makeCategoryRepo() {
        return {
            observeCategories: (parentId) => categoriesObs.observe(parentId, () => childrenCats(parentId)),
            async createCategory(input) {
                const fa = await guard("createCategory");
                if (fa)
                    return err(fa);
                const id = asCategoryId(newId());
                const siblings = childrenCats(input.parentId);
                const lastOrder = siblings.length ? siblings[siblings.length - 1].order : null;
                categories.set(id, {
                    id,
                    parentId: input.parentId,
                    name: input.name,
                    order: nextOrder(lastOrder),
                    collapsedBy: {},
                    ...(input.emoji !== undefined ? { emoji: input.emoji } : {}),
                    ...(input.kind !== undefined ? { kind: input.kind } : {}),
                });
                categoriesObs.emit(input.parentId, childrenCats(input.parentId));
                return ok(id);
            },
            async updateCategory(patch) {
                const fa = await guard("updateCategory");
                if (fa)
                    return err(fa);
                const c = categories.get(patch.categoryId);
                if (!c)
                    return err(notFound("category", patch.categoryId));
                if (patch.name !== undefined)
                    c.name = patch.name;
                if (patch.order !== undefined)
                    c.order = patch.order;
                applyNullable(c, "emoji", patch.emoji);
                categoriesObs.emit(c.parentId, childrenCats(c.parentId));
                return ok(undefined);
            },
            // Per-person collapse (Issue #240) — mirrors the Firestore repo: an expand removes
            // the entry instead of storing `false`.
            async setCollapsed(categoryId, userId, collapsed) {
                const fa = await guard("setCollapsed");
                if (fa)
                    return err(fa);
                const c = categories.get(categoryId);
                if (!c)
                    return err(notFound("category", categoryId));
                const next = { ...c.collapsedBy };
                if (collapsed)
                    next[userId] = true;
                else
                    delete next[userId];
                c.collapsedBy = next;
                categoriesObs.emit(c.parentId, childrenCats(c.parentId));
                return ok(undefined);
            },
            async reorderCategory(categoryId, order) {
                const fa = await guard("reorderCategory");
                if (fa)
                    return err(fa);
                const c = categories.get(categoryId);
                if (!c)
                    return err(notFound("category", categoryId));
                c.order = order;
                categoriesObs.emit(c.parentId, childrenCats(c.parentId));
                return ok(undefined);
            },
            async deleteCategory(categoryId) {
                const fa = await guard("deleteCategory");
                if (fa)
                    return err(fa);
                const c = categories.get(categoryId);
                categories.delete(categoryId);
                if (c)
                    categoriesObs.emit(c.parentId, childrenCats(c.parentId));
                return ok(undefined);
            },
        };
    }
    function makeItemRepo() {
        const emit = (tripId) => itemsObs.emit(tripId, itemsOfTrip(tripId));
        return {
            observeItems: (tripId) => itemsObs.observe(tripId, () => itemsOfTrip(tripId)),
            async createItem(input) {
                const fa = await guard("createItem");
                if (fa)
                    return err(fa);
                const cat = categories.get(input.categoryId);
                if (!cat)
                    return err(notFound("category", input.categoryId));
                const id = asItemId(newId());
                const assignment = { kind: "everyone" };
                const it = {
                    id,
                    categoryId: input.categoryId,
                    name: input.name,
                    quantity: input.quantity,
                    ...(input.description !== undefined ? { description: input.description } : {}),
                    ...(input.notes !== undefined ? { notes: input.notes } : {}),
                    ...(input.tags !== undefined && input.tags.length > 0 ? { tags: input.tags } : {}),
                    assignment,
                    completions: emptyCompletion(),
                    order: nextOrder(lastItemOrder(input.categoryId)),
                };
                items.set(id, it);
                itemParent.set(id, cat.parentId);
                emit(cat.parentId);
                return ok(id);
            },
            async updateItem(patch) {
                const fa = await guard("updateItem");
                if (fa)
                    return err(fa);
                const it = items.get(patch.itemId);
                if (!it)
                    return err(notFound("item", patch.itemId));
                if (patch.name !== undefined)
                    it.name = patch.name;
                if (patch.quantity !== undefined)
                    it.quantity = patch.quantity;
                applyNullable(it, "description", patch.description);
                applyNullable(it, "notes", patch.notes);
                // Tags are stored absent rather than empty, so an emptied list clears them (T29).
                applyNullable(it, "tags", tagsOrNull(patch.tags));
                // Only the cached flag, and only when the row actually has provenance (Doc 02 §7).
                if (patch.modifiedSinceImport !== undefined && it.provenance) {
                    it.provenance = { ...it.provenance, modifiedSinceImport: patch.modifiedSinceImport };
                }
                emit(itemParent.get(it.id));
                return ok(undefined);
            },
            async setAssignment(itemId, assignment) {
                const fa = await guard("setAssignment");
                if (fa)
                    return err(fa);
                const it = items.get(itemId);
                if (!it)
                    return err(notFound("item", itemId));
                it.assignment = assignment; // completions are a view-independent record; leave them untouched
                emit(itemParent.get(it.id));
                return ok(undefined);
            },
            async setCompletion(itemId, userId, done) {
                const fa = await guard("setCompletion");
                if (fa)
                    return err(fa);
                const it = items.get(itemId);
                if (!it)
                    return err(notFound("item", itemId));
                if (done)
                    it.completions.byParticipant[userId] = { done: true, checkedBy: userId, at: new Date() };
                else
                    delete it.completions.byParticipant[userId];
                emit(itemParent.get(it.id));
                return ok(undefined);
            },
            async setStaging(itemId, userId, done) {
                const fa = await guard("setStaging");
                if (fa)
                    return err(fa);
                const it = items.get(itemId);
                if (!it)
                    return err(notFound("item", itemId));
                it.staging ??= emptyCompletion();
                if (done)
                    it.staging.byParticipant[userId] = { done: true, checkedBy: userId, at: new Date() };
                else
                    delete it.staging.byParticipant[userId];
                emit(itemParent.get(it.id));
                return ok(undefined);
            },
            async moveItem(itemId, toCategoryId, order) {
                const fa = await guard("moveItem");
                if (fa)
                    return err(fa);
                const it = items.get(itemId);
                const cat = categories.get(toCategoryId);
                if (!it || !cat)
                    return err(notFound("item", itemId));
                const oldParent = itemParent.get(it.id);
                it.categoryId = toCategoryId;
                it.order = order;
                itemParent.set(it.id, cat.parentId);
                emit(oldParent);
                if (cat.parentId !== oldParent)
                    emit(cat.parentId);
                return ok(undefined);
            },
            async deleteItem(itemId) {
                const fa = await guard("deleteItem");
                if (fa)
                    return err(fa);
                const parent = itemParent.get(itemId);
                items.delete(itemId);
                itemParent.delete(itemId);
                if (parent)
                    emit(parent);
                return ok(undefined);
            },
        };
    }
    function makeTodoRepo() {
        const emit = (tripId) => todosObs.emit(tripId, todosOfTrip(tripId));
        return {
            observeTodos: (tripId) => todosObs.observe(tripId, () => todosOfTrip(tripId)),
            async createTodo(input) {
                const fa = await guard("createTodo");
                if (fa)
                    return err(fa);
                const cat = categories.get(input.categoryId);
                if (!cat)
                    return err(notFound("category", input.categoryId));
                const id = asTodoId(newId());
                const td = {
                    id,
                    categoryId: input.categoryId,
                    name: input.name,
                    ...(input.description !== undefined ? { description: input.description } : {}),
                    ...(input.notes !== undefined ? { notes: input.notes } : {}),
                    ...(input.tags !== undefined && input.tags.length > 0 ? { tags: input.tags } : {}),
                    assignment: { kind: "everyone" },
                    completions: emptyCompletion(),
                    ...(input.dueDate ? { dueDate: input.dueDate } : {}),
                    dueDateManuallyOverridden: false,
                    remindersSentTo: {},
                    reminderComplete: false,
                    order: nextOrder(lastTodoOrder(input.categoryId)),
                };
                todos.set(id, td);
                todoParent.set(id, cat.parentId);
                emit(cat.parentId);
                return ok(id);
            },
            async updateTodo(patch) {
                const fa = await guard("updateTodo");
                if (fa)
                    return err(fa);
                const td = todos.get(patch.todoId);
                if (!td)
                    return err(notFound("todo", patch.todoId));
                if (patch.name !== undefined)
                    td.name = patch.name;
                applyNullable(td, "description", patch.description);
                applyNullable(td, "notes", patch.notes);
                applyNullable(td, "tags", tagsOrNull(patch.tags));
                if (patch.dueDate !== undefined)
                    applyNullable(td, "dueDate", patch.dueDate);
                if (patch.relativeDueOffset !== undefined)
                    applyNullable(td, "relativeDueOffset", patch.relativeDueOffset);
                // Only the cached flag, and only when the row actually has provenance (Doc 02 §7).
                if (patch.modifiedSinceImport !== undefined && td.provenance) {
                    td.provenance = { ...td.provenance, modifiedSinceImport: patch.modifiedSinceImport };
                }
                emit(todoParent.get(td.id));
                return ok(undefined);
            },
            async setCompletion(todoId, participantId, done) {
                const fa = await guard("setCompletion");
                if (fa)
                    return err(fa);
                const td = todos.get(todoId);
                if (!td)
                    return err(notFound("todo", todoId));
                if (done)
                    td.completions.byParticipant[participantId] = { done: true, checkedBy: participantId, at: new Date() };
                else
                    delete td.completions.byParticipant[participantId];
                emit(todoParent.get(td.id));
                return ok(undefined);
            },
            async setAssignment(todoId, assignment) {
                const fa = await guard("setAssignment");
                if (fa)
                    return err(fa);
                const td = todos.get(todoId);
                if (!td)
                    return err(notFound("todo", todoId));
                td.assignment = assignment; // todos have no per-participant completions to reconcile
                emit(todoParent.get(td.id));
                return ok(undefined);
            },
            async pinDueDate(todoId, dueDate) {
                const fa = await guard("pinDueDate");
                if (fa)
                    return err(fa);
                const td = todos.get(todoId);
                if (!td)
                    return err(notFound("todo", todoId));
                td.dueDate = dueDate;
                td.dueDateManuallyOverridden = true;
                emit(todoParent.get(td.id));
                return ok(undefined);
            },
            async moveTodo(todoId, toCategoryId, order) {
                const fa = await guard("moveTodo");
                if (fa)
                    return err(fa);
                const td = todos.get(todoId);
                const cat = categories.get(toCategoryId);
                if (!td || !cat)
                    return err(notFound("todo", todoId));
                const oldParent = todoParent.get(td.id);
                td.categoryId = toCategoryId;
                td.order = order;
                todoParent.set(td.id, cat.parentId);
                emit(oldParent);
                if (cat.parentId !== oldParent)
                    emit(cat.parentId);
                return ok(undefined);
            },
            async deleteTodo(todoId) {
                const fa = await guard("deleteTodo");
                if (fa)
                    return err(fa);
                const parent = todoParent.get(todoId);
                todos.delete(todoId);
                todoParent.delete(todoId);
                if (parent)
                    emit(parent);
                return ok(undefined);
            },
        };
    }
    function makeTemplateRepo() {
        // Every member sees the change: the summary list is the only stream a template has.
        const emitTemplate = (t) => {
            for (const u of Object.keys(t.members))
                myTemplatesObs.emit(`${u}:${t.archived}`, myTemplates(u, t.archived));
        };
        return {
            observeMyTemplates: (userId, opts) => myTemplatesObs.observe(`${userId}:${opts.archived}`, () => myTemplates(userId, opts.archived)),
            async searchTemplates(userId, query) {
                const fa = await guard("searchTemplates");
                if (fa)
                    return err(fa);
                const q = query.trim().toLowerCase();
                return ok(myTemplates(userId, false).filter((t) => t.name.toLowerCase().includes(q)));
            },
            async createTemplate(input) {
                const fa = await guard("createTemplate");
                if (fa)
                    return err(fa);
                const id = asTemplateId(newId());
                templates.set(id, {
                    id,
                    name: input.name,
                    ...(input.emoji !== undefined ? { emoji: input.emoji } : {}),
                    ...(input.color !== undefined ? { color: input.color } : {}),
                    archived: false,
                    members: { [me()]: { role: "owner" } },
                    participants: clone(input.participants ?? {}),
                });
                myTemplatesObs.emit(`${me()}:false`, myTemplates(me(), false));
                return ok(id);
            },
            async fetchTemplateContents(id) {
                const fa = await guard("fetchTemplateContents");
                if (fa)
                    return err(fa);
                if (!templates.has(id))
                    return err(notFound("template", id));
                // Template contents live in the same tables as trip contents, keyed by the
                // template as parent (Doc 07 §2) — project them into the planner's shape.
                return ok({
                    templateId: id,
                    categories: childrenCats(id).map((c) => ({ id: c.id, name: c.name })),
                    items: itemsOfTrip(id).map((i) => ({
                        id: i.id,
                        categoryId: i.categoryId,
                        name: i.name,
                        quantity: i.quantity,
                        ...(i.description !== undefined ? { description: i.description } : {}),
                        ...(i.notes !== undefined ? { notes: i.notes } : {}),
                        assignment: clone(i.assignment),
                        // Issue #188 — a template checkbox only stages; the import carries it over.
                        ...(i.staging !== undefined ? { staging: clone(i.staging) } : {}),
                    })),
                    todos: todosOfTrip(id).map((t) => ({
                        id: t.id,
                        categoryId: t.categoryId,
                        name: t.name,
                        ...(t.description !== undefined ? { description: t.description } : {}),
                        ...(t.notes !== undefined ? { notes: t.notes } : {}),
                        assignment: clone(t.assignment),
                        ...(t.relativeDueOffset !== undefined ? { relativeDueOffset: clone(t.relativeDueOffset) } : {}),
                    })),
                    // The default travelers the import turns into trip members (issue #185).
                    participants: Object.entries(templates.get(id).participants).map(([participantId, participant]) => ({
                        id: participantId,
                        participant: clone(participant),
                    })),
                });
            },
            // Same maps as `fetchTemplateContents`, unprojected: sync-back compares whole
            // domain rows (Doc 13 §5).
            async fetchTemplateRows(id) {
                const fa = await guard("fetchTemplateRows");
                if (fa)
                    return err(fa);
                if (!templates.has(id))
                    return err(notFound("template", id));
                return ok({ categories: childrenCats(id), items: itemsOfTrip(id), todos: todosOfTrip(id) });
            },
            // --- participants (issue #185) ---
            async addUserParticipant(id, user) {
                const fa = await guard("addUserParticipant");
                if (fa)
                    return err(fa);
                const t = templates.get(id);
                if (!t)
                    return err(notFound("template", id));
                // Keyed by the user id, so the same person added from two templates lands on
                // one trip member when both are imported.
                const participantId = user.userId;
                t.participants[participantId] = {
                    kind: "user",
                    userId: user.userId,
                    displayName: user.displayName,
                    email: user.email,
                    ...(user.photoUrl !== undefined ? { photoUrl: user.photoUrl } : {}),
                };
                emitTemplate(t);
                return ok(participantId);
            },
            async addManagedParticipant(id, displayName) {
                const fa = await guard("addManagedParticipant");
                if (fa)
                    return err(fa);
                const t = templates.get(id);
                if (!t)
                    return err(notFound("template", id));
                const participantId = asManagedProfileId(newId());
                t.participants[participantId] = { kind: "managed", displayName };
                emitTemplate(t);
                return ok(participantId);
            },
            async removeParticipant(id, participantId) {
                const fa = await guard("removeParticipant");
                if (fa)
                    return err(fa);
                const t = templates.get(id);
                if (!t)
                    return err(notFound("template", id));
                delete t.participants[participantId];
                emitTemplate(t);
                return ok(undefined);
            },
            async fetchTemplateParticipants(id) {
                const fa = await guard("fetchTemplateParticipants");
                if (fa)
                    return err(fa);
                const t = templates.get(id);
                if (!t)
                    return err(notFound("template", id));
                return ok(clone(t.participants));
            },
            async renameTemplate(id, name) {
                const fa = await guard("renameTemplate");
                if (fa)
                    return err(fa);
                const t = templates.get(id);
                if (!t)
                    return err(notFound("template", id));
                t.name = name;
                emitTemplate(t);
                return ok(undefined);
            },
            async setTemplateColor(id, color) {
                const fa = await guard("setTemplateColor");
                if (fa)
                    return err(fa);
                const t = templates.get(id);
                if (!t)
                    return err(notFound("template", id));
                t.color = color;
                emitTemplate(t);
                return ok(undefined);
            },
            async setTemplateEmoji(id, emoji) {
                const fa = await guard("setTemplateEmoji");
                if (fa)
                    return err(fa);
                const t = templates.get(id);
                if (!t)
                    return err(notFound("template", id));
                t.emoji = emoji;
                emitTemplate(t);
                return ok(undefined);
            },
            async archiveTemplate(id, archived) {
                const fa = await guard("archiveTemplate");
                if (fa)
                    return err(fa);
                const t = templates.get(id);
                if (!t)
                    return err(notFound("template", id));
                t.archived = archived;
                for (const u of Object.keys(t.members)) {
                    myTemplatesObs.emit(`${u}:false`, myTemplates(u, false));
                    myTemplatesObs.emit(`${u}:true`, myTemplates(u, true));
                }
                return ok(undefined);
            },
            async deleteTemplate(id) {
                const fa = await guard("deleteTemplate");
                if (fa)
                    return err(fa);
                const t = templates.get(id);
                templates.delete(id);
                if (t)
                    for (const u of Object.keys(t.members)) {
                        myTemplatesObs.emit(`${u}:false`, myTemplates(u, false));
                        myTemplatesObs.emit(`${u}:true`, myTemplates(u, true));
                    }
                return ok(undefined);
            },
        };
    }
    function makeSharingRepo() {
        const resourceTrip = (r) => (r.kind === "trip" ? trips.get(r.id) : undefined);
        return {
            async shareWithUser(resource, userId, role) {
                const fa = await guard("shareWithUser");
                if (fa)
                    return err(fa);
                const t = resourceTrip(resource);
                if (!t)
                    return err(notFound(resource.kind, resource.id));
                const rec = users.get(userId);
                t.members[userId] = {
                    kind: "user",
                    role,
                    profile: { displayName: rec?.profile.displayName ?? "", email: rec?.profile.email ?? "" },
                    settings: { stagingOptIn: rec?.defaultStagingOptIn ?? false },
                };
                tripObs.emit(t.id, t);
                emitMyTrips(userId);
                return ok(undefined);
            },
            async changeRole(resource, userId, role) {
                const fa = await guard("changeRole");
                if (fa)
                    return err(fa);
                const t = resourceTrip(resource);
                const m = t?.members[userId];
                if (!t || !m || m.kind !== "user")
                    return err(notFound("member", userId));
                m.role = role;
                tripObs.emit(t.id, t);
                emitMyTrips(userId);
                return ok(undefined);
            },
            async removeMember(resource, userId) {
                const fa = await guard("removeMember");
                if (fa)
                    return err(fa);
                const t = resourceTrip(resource);
                if (!t)
                    return err(notFound(resource.kind, resource.id));
                delete t.members[userId];
                tripObs.emit(t.id, t);
                emitMyTrips(userId);
                return ok(undefined);
            },
            async renameGuest(resource, userId, displayName) {
                const fa = await guard("renameGuest");
                if (fa)
                    return err(fa);
                const t = resourceTrip(resource);
                const m = t?.members[userId];
                if (!t || !m || m.kind !== "user" || m.guest === undefined)
                    return err(notFound("guest", userId));
                m.profile = { ...m.profile, displayName };
                tripObs.emit(t.id, t);
                return ok(undefined);
            },
            async inviteByEmail(resource, email, role) {
                const fa = await guard("inviteByEmail");
                if (fa)
                    return err(fa);
                const id = newId();
                pendingInvites.set(id, { id, email: email.toLowerCase(), resource, role, invitedBy: me(), status: "pending", createdAt: new Date() });
                pendingInvitesObs.emit(refKey(resource), pendingFor(resource, me()));
                return ok(undefined);
            },
            observeMyPendingInvites: (resource) => pendingInvitesObs.observe(refKey(resource), () => pendingFor(resource, me())),
            async revokePendingInvite(inviteId) {
                const fa = await guard("revokePendingInvite");
                if (fa)
                    return err(fa);
                const inv = pendingInvites.get(inviteId);
                if (!inv)
                    return err(notFound("invite", inviteId));
                inv.status = "revoked";
                pendingInvitesObs.emit(refKey(inv.resource), pendingFor(inv.resource, inv.invitedBy));
                return ok(undefined);
            },
            // Invite inbox (T25): invites addressed to my email. Mirrors the callables —
            // fetch-based (no stream), accept adds membership, decline only marks the doc.
            async listIncomingInvites() {
                const fa = await guard("listIncomingInvites");
                if (fa)
                    return err(fa);
                const myEmail = users.get(me())?.profile.email.toLowerCase() ?? "";
                const mine = [...pendingInvites.values()].filter((p) => p.status === "pending" && p.email === myEmail);
                return ok(mine.map((p) => ({
                    id: p.id,
                    resource: p.resource,
                    role: p.role,
                    resourceName: (p.resource.kind === "trip" ? trips.get(p.resource.id)?.name : templates.get(p.resource.id)?.name) ?? "",
                    inviterName: users.get(p.invitedBy)?.profile.displayName ?? "",
                })));
            },
            async acceptInvite(inviteId) {
                const fa = await guard("acceptInvite");
                if (fa)
                    return err(fa);
                const inv = pendingInvites.get(inviteId);
                if (!inv)
                    return err(notFound("invite", inviteId));
                const u = users.get(me());
                if (!u || inv.email !== u.profile.email.toLowerCase())
                    return err(permissionDenied("accept this invite"));
                if (inv.status !== "pending")
                    return ok(undefined);
                inv.status = "claimed";
                if (inv.resource.kind === "trip") {
                    const t = trips.get(inv.resource.id);
                    if (t) {
                        t.members[me()] = {
                            kind: "user",
                            role: inv.role,
                            profile: { displayName: u.profile.displayName, email: u.profile.email },
                            settings: { stagingOptIn: u.defaultStagingOptIn },
                        };
                        tripObs.emit(t.id, t);
                        emitMyTrips(me());
                    }
                }
                else {
                    const tpl = templates.get(inv.resource.id);
                    if (tpl) {
                        tpl.members[me()] = { role: inv.role };
                        myTemplatesObs.emit(`${me()}:false`, myTemplates(me(), false));
                    }
                }
                pendingInvitesObs.emit(refKey(inv.resource), pendingFor(inv.resource, inv.invitedBy));
                return ok(undefined);
            },
            async declineInvite(inviteId) {
                const fa = await guard("declineInvite");
                if (fa)
                    return err(fa);
                const inv = pendingInvites.get(inviteId);
                if (!inv)
                    return err(notFound("invite", inviteId));
                const myEmail = users.get(me())?.profile.email.toLowerCase() ?? "";
                if (inv.email !== myEmail)
                    return err(permissionDenied("decline this invite"));
                inv.status = "declined";
                pendingInvitesObs.emit(refKey(inv.resource), pendingFor(inv.resource, inv.invitedBy));
                return ok(undefined);
            },
            async addManagedMember(resource, profileId) {
                const fa = await guard("addManagedMember");
                if (fa)
                    return err(fa);
                const t = resourceTrip(resource);
                const p = managed.get(profileId);
                if (!t || !p)
                    return err(notFound("managedProfile", profileId));
                t.members[profileId] = {
                    kind: "managed",
                    guardianUserIds: [...p.guardianUserIds],
                    profile: { displayName: p.displayName },
                    settings: { stagingOptIn: false },
                };
                tripObs.emit(t.id, t);
                return ok(undefined);
            },
            async addManagedMemberByName(resource, displayName) {
                const fa = await guard("addManagedMemberByName");
                if (fa)
                    return err(fa);
                const t = resourceTrip(resource);
                if (!t)
                    return err(notFound("trip", resource.id));
                // Trip-scoped: no top-level managedProfiles entry — the member lives only here,
                // guarded by its creator.
                const id = asManagedProfileId(newId());
                t.members[id] = {
                    kind: "managed",
                    guardianUserIds: [me()],
                    profile: { displayName },
                    settings: { stagingOptIn: false },
                };
                tripObs.emit(t.id, t);
                return ok(id);
            },
            async createInviteLink(resource, role, opts) {
                const fa = await guard("createInviteLink");
                if (fa)
                    return err(fa);
                const link = {
                    token: newId(),
                    resource,
                    role,
                    allowJoin: opts?.allowJoin ?? false,
                    createdBy: me(),
                    revoked: false,
                    ...(opts?.expiresAt ? { expiresAt: opts.expiresAt } : {}),
                    createdAt: new Date(),
                };
                inviteLinks.set(link.token, link);
                inviteLinksObs.emit(refKey(resource), linksFor(resource));
                return ok(link);
            },
            observeInviteLinks: (resource) => inviteLinksObs.observe(refKey(resource), () => linksFor(resource)),
            async revokeInviteLink(token) {
                const fa = await guard("revokeInviteLink");
                if (fa)
                    return err(fa);
                const l = inviteLinks.get(token);
                if (!l)
                    return err(notFound("inviteLink", token));
                // T31: revoking a joinable link closes the door to NEW joins — it does not evict
                // the guests it already admitted; the owner removes those like any other member.
                // (Revoking a *view-only* link does drop its grants, but the fake models no
                // grants — see the onShareLinkRevoked trigger.)
                l.revoked = true;
                inviteLinksObs.emit(refKey(l.resource), linksFor(l.resource));
                return ok(undefined);
            },
            async viewViaLink(token) {
                const fa = await guard("viewViaLink");
                if (fa)
                    return err(fa);
                const l = inviteLinks.get(token);
                if (!l || l.revoked)
                    return err(notFound("inviteLink", token));
                // Read-only access grant, NOT membership; allowJoin drives the Join affordance.
                return ok({ resource: l.resource, allowJoin: l.allowJoin });
            },
            async joinViaLink(token, displayName) {
                const fa = await guard("joinViaLink");
                if (fa)
                    return err(fa);
                const l = inviteLinks.get(token);
                if (!l || l.revoked)
                    return err(notFound("inviteLink", token));
                if (!l.allowJoin)
                    return err(conflict("this link is view-only"));
                const u = users.get(me());
                if (!u)
                    return err(conflict("joining requires a session"));
                // T31: an anonymous session joins as a guest — link-scoped membership under the
                // name it typed, no email. Without a name there is no identity to record.
                const isAnonymous = u.anonymous === true;
                const guestName = (displayName ?? "").trim();
                if (isAnonymous && guestName === "")
                    return err(conflict("joining anonymously requires a name"));
                const t = resourceTrip(l.resource);
                if (t) {
                    t.members[me()] = isAnonymous
                        ? {
                            kind: "user",
                            role: l.role,
                            profile: { displayName: guestName, email: "" },
                            settings: { stagingOptIn: false },
                            guest: { linkToken: token },
                        }
                        : {
                            kind: "user",
                            role: l.role,
                            profile: { displayName: u.profile.displayName, email: u.profile.email },
                            settings: { stagingOptIn: u.defaultStagingOptIn },
                        };
                    tripObs.emit(t.id, t);
                    emitMyTrips(me());
                }
                return ok(l.resource);
            },
        };
    }
    // ---- small local helpers needing the tables ----
    function lastItemOrder(categoryId) {
        const its = [...items.values()].filter((i) => i.categoryId === categoryId).sort(compareByOrder);
        return its.length ? its[its.length - 1].order : null;
    }
    function lastTodoOrder(categoryId) {
        const tds = [...todos.values()].filter((i) => i.categoryId === categoryId).sort(compareByOrder);
        return tds.length ? tds[tds.length - 1].order : null;
    }
    function firstNoteOrder(listId) {
        const ns = [...notes.values()].filter((n) => n.listId === listId).sort(compareByOrder);
        return ns.length ? ns[0].order : null;
    }
    // The per-row detach/delete/makeIndependent actions `applyTemplateRemovalPlan`
    // computes for one trip's rows (Doc 07 §5 decision table).
    function applyRowActions(actions) {
        for (const a of actions) {
            if (a.collection === "items") {
                const it = items.get(a.id);
                if (!it)
                    continue;
                // Captured before a `delete` clears the lookup out from under it.
                const parent = itemParent.get(it.id);
                if (a.kind === "delete") {
                    items.delete(it.id);
                    itemParent.delete(it.id);
                }
                else if (a.kind === "makeIndependent") {
                    delete it.provenance;
                    delete it.templateIds;
                }
                else {
                    it.provenance = a.provenance;
                    it.templateIds = a.templateIds;
                }
                if (parent)
                    itemsObs.emit(parent, itemsOfTrip(parent));
            }
            else {
                const td = todos.get(a.id);
                if (!td)
                    continue;
                const parent = todoParent.get(td.id);
                if (a.kind === "delete") {
                    todos.delete(td.id);
                    todoParent.delete(td.id);
                }
                else if (a.kind === "makeIndependent") {
                    delete td.provenance;
                    delete td.templateIds;
                }
                else {
                    td.provenance = a.provenance;
                    td.templateIds = a.templateIds;
                }
                if (parent)
                    todosObs.emit(parent, todosOfTrip(parent));
            }
        }
    }
    const bundle = {
        auth,
        profiles,
        friends,
        managed: managedRepo,
        trips: tripRepo,
        notes: noteRepo,
        categories: categoryRepo,
        items: itemRepo,
        todos: todoRepo,
        templates: templateRepo,
        sharing: sharingRepo,
        connectivity,
        failNext: (method, error) => failures.set(method, error),
        setLatency: (ms) => {
            latency = ms;
        },
        seedUser: (input) => createUser(input.email, input.password, input.displayName),
        verifyEmail: (userId) => {
            const rec = users.get(userId);
            if (rec)
                rec.emailVerified = true;
        },
        seedProviderIdentity: (input) => providerIdentities.set(providerIdOf(input.provider), { email: input.email, displayName: input.displayName }),
        applyImportPlan: (plan, tripId) => {
            if (tripId) {
                const t = trips.get(tripId);
                if (t) {
                    t.importedTemplateIds = plan.tripPatch.importedTemplateIds;
                    // Issue #185 — the templates' participants join the trip as members.
                    for (const { participantId, entry } of plan.memberCreates)
                        t.members[participantId] = entry;
                    tripObs.emit(tripId, t);
                    if (plan.memberCreates.length > 0)
                        emitTripMembers(t);
                }
            }
            for (const c of plan.categoryCreates) {
                categories.set(c.id, c);
                categoriesObs.emit(c.parentId, childrenCats(c.parentId));
            }
            for (const it of plan.itemCreates) {
                items.set(it.id, it);
                const cat = categories.get(it.categoryId);
                if (cat) {
                    itemParent.set(it.id, cat.parentId);
                    itemsObs.emit(cat.parentId, itemsOfTrip(cat.parentId));
                }
            }
            for (const td of plan.todoCreates) {
                todos.set(td.id, td);
                const cat = categories.get(td.categoryId);
                if (cat) {
                    todoParent.set(td.id, cat.parentId);
                    todosObs.emit(cat.parentId, todosOfTrip(cat.parentId));
                }
            }
            // Doc 14 §4 — a refresh's in-place updates, applied before the provenance patches
            // that rebase the same rows.
            if ("itemUpdates" in plan) {
                for (const u of plan.itemUpdates) {
                    const it = items.get(u.id);
                    if (!it)
                        continue;
                    items.set(u.id, applyRowPatch(it, u.patch));
                    const parent = itemParent.get(u.id);
                    if (parent)
                        itemsObs.emit(parent, itemsOfTrip(parent));
                }
                for (const u of plan.todoUpdates) {
                    const td = todos.get(u.id);
                    if (!td)
                        continue;
                    todos.set(u.id, applyRowPatch(td, u.patch));
                    const parent = todoParent.get(u.id);
                    if (parent)
                        todosObs.emit(parent, todosOfTrip(parent));
                }
            }
            // Provenance patches target *pre-existing* rows that a re-import deduped onto
            // (they gained a new template source), so they're separate from the create
            // loops above — and a single patch may address an item or a todo, hence the
            // `collection` discriminator rather than folding it into the item loop.
            for (const patch of plan.provenancePatches) {
                if (patch.collection === "items") {
                    const it = items.get(patch.id);
                    if (it) {
                        it.provenance = patch.provenance;
                        it.templateIds = patch.templateIds;
                        const p = itemParent.get(it.id);
                        if (p)
                            itemsObs.emit(p, itemsOfTrip(p));
                    }
                }
                else {
                    const td = todos.get(patch.id);
                    if (td) {
                        td.provenance = patch.provenance;
                        td.templateIds = patch.templateIds;
                        const p = todoParent.get(td.id);
                        if (p)
                            todosObs.emit(p, todosOfTrip(p));
                    }
                }
            }
        },
        // Issue #8 — the fake counterpart of `applyCopyPlan`. The plan's rows already
        // carry fresh ids and point at the copied categories, so applying it is a plain
        // insert; `itemParent`/`todoParent` are derived from the category, exactly as the
        // import applier does.
        applyCopyPlan: async (plan) => {
            const fa = await guard("applyCopyPlan");
            if (fa)
                return err(fa);
            for (const c of plan.categoryCreates)
                categories.set(c.id, c);
            for (const it of plan.itemCreates) {
                items.set(it.id, it);
                const cat = categories.get(it.categoryId);
                if (cat)
                    itemParent.set(it.id, cat.parentId);
            }
            for (const td of plan.todoCreates) {
                todos.set(td.id, td);
                const cat = categories.get(td.categoryId);
                if (cat)
                    todoParent.set(td.id, cat.parentId);
            }
            // One emit per collection at the end: the target list is created and populated
            // as a unit, so subscribers should never see it half-filled.
            categoriesObs.emit(plan.targetId, childrenCats(plan.targetId));
            itemsObs.emit(plan.targetId, itemsOfTrip(plan.targetId));
            todosObs.emit(plan.targetId, todosOfTrip(plan.targetId));
            return ok(undefined);
        },
        // The fake counterpart of `applyTemplateRemovalPlan`: per-row decision-table
        // actions (Doc 07 §5), scoped to one trip's rows, plus the trip's own
        // `importedTemplateIds` patch that drops the removed template.
        applyTemplateRemovalPlan: (plan, tripId) => {
            applyRowActions(plan.actions);
            const t = trips.get(tripId);
            if (t) {
                t.importedTemplateIds = plan.tripPatch.importedTemplateIds;
                tripObs.emit(tripId, t);
            }
        },
        // Issue #185 — the fake counterpart of `applyParticipantRemovalPlan`: rows the
        // departing traveler was the sole allocatee of go, the rest shed that allocation
        // and any check they had recorded.
        applyParticipantRemovalPlan: async (plan) => {
            const fa = await guard("applyParticipantRemovalPlan");
            if (fa)
                return err(fa);
            const emitItem = (id) => {
                const p = itemParent.get(id);
                if (p)
                    itemsObs.emit(p, itemsOfTrip(p));
            };
            const emitTodo = (id) => {
                const p = todoParent.get(id);
                if (p)
                    todosObs.emit(p, todosOfTrip(p));
            };
            for (const d of plan.deletes) {
                if (d.collection === "items") {
                    const id = d.id;
                    if (!items.delete(id))
                        continue;
                    emitItem(id);
                    itemParent.delete(id);
                }
                else {
                    const id = d.id;
                    if (!todos.delete(id))
                        continue;
                    emitTodo(id);
                    todoParent.delete(id);
                }
            }
            for (const patch of plan.patches) {
                if (patch.collection === "items") {
                    const it = items.get(patch.id);
                    if (!it)
                        continue;
                    if (patch.assignment !== undefined)
                        it.assignment = patch.assignment;
                    if (patch.completions !== undefined)
                        it.completions = patch.completions;
                    if (patch.staging !== undefined)
                        it.staging = patch.staging;
                    emitItem(it.id);
                }
                else {
                    const td = todos.get(patch.id);
                    if (!td)
                        continue;
                    if (patch.assignment !== undefined)
                        td.assignment = patch.assignment;
                    if (patch.completions !== undefined)
                        td.completions = patch.completions;
                    emitTodo(td.id);
                }
            }
            return ok(undefined);
        },
        // Doc 13 §5 — the fake counterpart of `applyTemplateSyncPlan`. Writes land on the
        // template's own rows (their category's parentId is the template) plus a
        // provenance patch on each affected trip row.
        applyTemplateSyncPlan: async (plan) => {
            const fa = await guard("applyTemplateSyncPlan");
            if (fa)
                return err(fa);
            const emitCats = (parentId) => categoriesObs.emit(parentId, childrenCats(parentId));
            const emitItem = (id) => {
                const p = itemParent.get(id);
                if (p)
                    itemsObs.emit(p, itemsOfTrip(p));
            };
            const emitTodo = (id) => {
                const p = todoParent.get(id);
                if (p)
                    todosObs.emit(p, todosOfTrip(p));
            };
            for (const c of plan.categoryCreates) {
                categories.set(c.id, c);
                emitCats(c.parentId);
            }
            for (const r of plan.categoryRenames) {
                const c = categories.get(r.id);
                if (!c)
                    continue;
                c.name = r.name;
                emitCats(c.parentId);
            }
            for (const it of plan.itemCreates) {
                items.set(it.id, it);
                const cat = categories.get(it.categoryId);
                if (cat)
                    itemParent.set(it.id, cat.parentId);
                emitItem(it.id);
            }
            for (const u of plan.itemUpdates) {
                const it = items.get(u.id);
                if (!it)
                    continue;
                if (u.patch.name !== undefined)
                    it.name = u.patch.name;
                if (u.patch.quantity !== undefined)
                    it.quantity = u.patch.quantity;
                applyNullable(it, "description", u.patch.description);
                applyNullable(it, "notes", u.patch.notes);
                applyNullable(it, "tags", tagsOrNull(u.patch.tags));
                if (u.patch.assignment !== undefined)
                    it.assignment = u.patch.assignment;
                emitItem(it.id);
            }
            for (const id of plan.itemDeletes) {
                const parent = itemParent.get(id);
                items.delete(id);
                itemParent.delete(id);
                if (parent)
                    itemsObs.emit(parent, itemsOfTrip(parent));
            }
            for (const td of plan.todoCreates) {
                todos.set(td.id, td);
                const cat = categories.get(td.categoryId);
                if (cat)
                    todoParent.set(td.id, cat.parentId);
                emitTodo(td.id);
            }
            for (const u of plan.todoUpdates) {
                const td = todos.get(u.id);
                if (!td)
                    continue;
                if (u.patch.name !== undefined)
                    td.name = u.patch.name;
                applyNullable(td, "description", u.patch.description);
                applyNullable(td, "notes", u.patch.notes);
                applyNullable(td, "tags", tagsOrNull(u.patch.tags));
                if (u.patch.assignment !== undefined)
                    td.assignment = u.patch.assignment;
                applyNullable(td, "relativeDueOffset", u.patch.relativeDueOffset);
                emitTodo(td.id);
            }
            for (const id of plan.todoDeletes) {
                const parent = todoParent.get(id);
                todos.delete(id);
                todoParent.delete(id);
                if (parent)
                    todosObs.emit(parent, todosOfTrip(parent));
            }
            // Category deletes come last: the planner only emits one when every row inside
            // it is going too (Doc 13 §5), and those rows are gone by now.
            for (const id of plan.categoryDeletes) {
                const c = categories.get(id);
                categories.delete(id);
                if (c)
                    emitCats(c.parentId);
            }
            for (const patch of plan.tripProvenancePatches) {
                if (patch.collection === "items") {
                    const it = items.get(patch.id);
                    if (!it)
                        continue;
                    it.provenance = patch.provenance;
                    it.templateIds = patch.templateIds;
                    emitItem(it.id);
                }
                else {
                    const td = todos.get(patch.id);
                    if (!td)
                        continue;
                    td.provenance = patch.provenance;
                    td.templateIds = patch.templateIds;
                    emitTodo(td.id);
                }
            }
            return ok(undefined);
        },
    };
    return bundle;
};
// Append after the last sibling (fractional order); first child if none.
const nextOrder = (last) => between(last, null);
// Apply a nullable field-level patch: undefined = leave, null = clear, value = set.
// Mirrors the Firestore repos' `tagsPatch`: an empty tag list clears the field (T29).
function tagsOrNull(tags) {
    return tags === undefined ? undefined : tags === null || tags.length === 0 ? null : tags;
}
function applyNullable(target, key, value) {
    if (value === undefined)
        return;
    const t = target;
    if (value === null)
        delete t[key];
    else
        t[key] = value;
}
//# sourceMappingURL=inMemory.js.map